import { useCallback, useEffect, useRef, useState } from 'react';
import { COUNTRY_CODE_TO_LOCALE_MAP, type CountryCodeToLocaleMap } from '@zep-us/i18n';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { logger } from '@zep/core-logger';
import { useCmsNewMapBannerContent } from '@/api/cms';
import { type NewMapBannerContent, type NewMapBannerItem } from '@/api/zepetoful.api/model';
import { useLocale } from '@/hooks';
import { useCountryCode } from '@/shared/hooks';
import { cn } from '@/utils/tailwind.util';
import { BannerIndex } from './BannerIndex';
import { ImageSection } from './ImageSection';
import { TextSection } from './TextSection';
export type NewMapBannerSize = 'sm' | 'md';
const BANNER_CHANGE_INTERVAL = 5000;
const BANNER_MAX_INDEX = 8;
const NewMapBannerComponent = ({
  data,
  size
}: {
  data: NewMapBannerItem[];
  size: NewMapBannerSize;
}) => {
  const [bannerIndex, setBannerIndex] = useState<number>(0);
  const {
    id,
    image,
    imageObject,
    name,
    desc,
    backgroundColor,
    preview
  } = data[bannerIndex];
  const {
    locale
  } = useLocale();
  const {
    countryCode
  } = useCountryCode();
  const preloadContainerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const isPublicPage = router.pathname === '/public';
  const isQuizDetailPage = router.pathname === '/quiz/[id]';
  const previewUrl = preview?.[COUNTRY_CODE_TO_LOCALE_MAP[countryCode as keyof CountryCodeToLocaleMap]] || preview?.en;
  const addLocaleToUrl = useCallback((url: string) => {
    return url.replace('https://quiz.zep.us', `https://quiz.zep.us/${locale}`);
  }, [locale]);
  const handleClick = () => {
    logger.track('new_map_banner_click', {
      map: id,
      location: isPublicPage ? 'public' : isQuizDetailPage ? 'quiz-detail' : 'unknown'
      // isAdmin·userType 은 logger.setUserContext 가 모든 스팬에 싣는 global prop 이라
      // 여기서 다시 넣지 않는다(각각 user.admin / user.type 으로 값이 같았다).
    });
  };
  useEffect(() => {
    if (data.length <= 1) return;
    const bannerChangeInterval = setInterval(() => {
      setBannerIndex(prevIndex => (prevIndex + 1) % data.length);
    }, BANNER_CHANGE_INTERVAL);
    return () => clearInterval(bannerChangeInterval);
  }, [data.length]);
  useEffect(() => {
    if (!preloadContainerRef.current) return;
    const preloadImages = () => {
      data.forEach(item => {
        const mainImage = new Image();
        mainImage.src = item.image;
        const objectImage = new Image();
        objectImage.src = item.imageObject;
      });
    };
    preloadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>
      <div ref={preloadContainerRef} className="hidden" />
      <Link href={addLocaleToUrl(previewUrl || `/${locale}`)} className="w-full" target="_blank" rel="noopener noreferrer" onClick={handleClick} data-sentry-element="Link" data-sentry-source-file="NewMapBanner.tsx">
        <div className={cn('w-full h-[90px] flex rounded-md relative outline outline-[1px] outline-opacity-black-1 outline-offset-[-1px]', size === 'sm' ? 'h-[66px] rounded-sm' : '')}>
          <ImageSection mainImage={image} decorationImage={imageObject} backgroundColor={backgroundColor} size={size} data-sentry-element="ImageSection" data-sentry-source-file="NewMapBanner.tsx" />
          <TextSection title={name} description={desc} backgroundColor={backgroundColor} size={size} data-sentry-element="TextSection" data-sentry-source-file="NewMapBanner.tsx" />
          {data.length > 1 && <BannerIndex index={bannerIndex + 1} total={data.length} />}
        </div>
      </Link>
    </>;
};
export const NewMapBanner = ({
  size = 'md'
}: {
  size?: NewMapBannerSize;
}) => {
  const {
    data
  } = useCmsNewMapBannerContent();
  const {
    locale
  } = useLocale();
  const bannerContent = data;
  const localeBannerItems = bannerContent?.[locale as keyof NewMapBannerContent];
  const preparedBannerItems = localeBannerItems?.filter(item => item.visible).slice(0, BANNER_MAX_INDEX);
  if (!preparedBannerItems || preparedBannerItems.length === 0) {
    return null;
  }
  return <NewMapBannerComponent data={preparedBannerItems} size={size} data-sentry-element="NewMapBannerComponent" data-sentry-component="NewMapBanner" data-sentry-source-file="NewMapBanner.tsx" />;
};